"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { updateThemePreference } from "@/lib/actions/profile-actions";
import { useAuth } from "@/providers/auth-provider";

// ─── Types ───────────────────────────────────────────────

type Theme = "light" | "dark" | "system";
interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ─── Constants ───────────────────────────────────────────

const STORAGE_KEY = "iloris-theme";

// ─── Helpers ─────────────────────────────────────────────

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function applyThemeClass(resolved: "light" | "dark") {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}
function resolveTheme(theme: Theme): "light" | "dark" {
  return theme === "system" ? getSystemTheme() : theme;
}

// ─── Provider ────────────────────────────────────────────

interface ThemeProviderProps {
  children: ReactNode;
}

// Theme state is localStorage-first; the inline pre-paint script in
// layout.tsx already applies the stored theme before hydration. There is
// deliberately NO server-provided theme: the root layout must stay free of
// request state (cookies/DB) so every route can be statically cached.
// Authenticated-ness — used only to decide whether a theme change also
// persists to the user's profile — comes from the client-side AuthProvider,
// which this provider is nested inside.
//
// Known, accepted trade-off: profiles.themePreference is now write-only from
// the theme path — a logged-in user on a brand-new device starts from the
// default (dark) instead of adopting their stored DB preference, until they
// toggle the theme (which re-persists it). Reading it back client-side would
// need a profile-fetch → setTheme round trip for an edge case that only
// affects first paint on a fresh device.
export function ThemeProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const {
    user
  } = useAuth();
  const isAuthenticated = Boolean(user);
  const [theme, setThemeState] = useState(_temp);
  let t1;
  if ($[0] !== theme) {
    t1 = () => resolveTheme(theme);
    $[0] = theme;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [resolvedTheme, setResolvedTheme] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== resolvedTheme) {
    t2 = () => {
      applyThemeClass(resolvedTheme);
    };
    t3 = [resolvedTheme];
    $[2] = resolvedTheme;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== theme) {
    t4 = () => {
      if (theme !== "system") {
        return;
      }
      const mql = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => {
        const resolved = resolveTheme("system");
        setResolvedTheme(resolved);
        applyThemeClass(resolved);
      };
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    };
    t5 = [theme];
    $[5] = theme;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== isAuthenticated) {
    t6 = newTheme => {
      setThemeState(newTheme);
      localStorage.setItem(STORAGE_KEY, newTheme);
      const resolved_0 = resolveTheme(newTheme);
      setResolvedTheme(resolved_0);
      applyThemeClass(resolved_0);
      if (isAuthenticated) {
        updateThemePreference(newTheme).catch(_temp2);
      }
    };
    $[8] = isAuthenticated;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const setTheme = t6;
  let t7;
  if ($[10] !== resolvedTheme || $[11] !== setTheme || $[12] !== theme) {
    t7 = {
      theme,
      resolvedTheme,
      setTheme
    };
    $[10] = resolvedTheme;
    $[11] = setTheme;
    $[12] = theme;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== children || $[15] !== t7) {
    t8 = <ThemeContext.Provider value={t7}>{children}</ThemeContext.Provider>;
    $[14] = children;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

// ─── Hook ────────────────────────────────────────────────
function _temp2() {}
function _temp() {
  if (typeof window === "undefined") {
    return "system";
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && ["light", "dark", "system"].includes(stored)) {
    return stored as Theme;
  }
  return "dark";
}
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}